'use client'

import { useEffect, useRef, useState } from 'react'

interface UseTripleseatFormProps {
  leadFormId: string
  publicKey: string
}

declare global {
  interface Window {
    grecaptcha?: unknown
  }
}

const TRIPLESEAT_SCRIPT_BASE = 'https://api.tripleseat.com/v1/leads/ts_script.js'
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js'

/**
 * Tripleseat's lead forms use Google reCAPTCHA. The widget is rendered on
 * submit via the global `window.grecaptcha` object. When the Tripleseat
 * script is loaded dynamically without reCAPTCHA also being present, you
 * get a `ReferenceError: grecaptcha is not defined` at submit time.
 *
 * Inject Google's `api.js` once into the document so `window.grecaptcha`
 * is available. It's idempotent: if the script is already on the page, we
 * skip it.
 */
const ensureRecaptchaScript = () => {
  if (typeof window === 'undefined') return
  if (window.grecaptcha) return
  if (document.querySelector(`script[src^="${RECAPTCHA_SCRIPT_SRC}"]`)) return

  const recaptchaScript = document.createElement('script')
  recaptchaScript.src = RECAPTCHA_SCRIPT_SRC
  recaptchaScript.async = true
  recaptchaScript.defer = true
  document.head.appendChild(recaptchaScript)
}

export const useTripleseatForm = ({ leadFormId, publicKey }: UseTripleseatFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!leadFormId || !publicKey || !containerRef.current) {
      console.error('Missing required Tripleseat form configuration:', {
        leadFormId: !!leadFormId,
        publicKey: !!publicKey,
        containerRef: !!containerRef.current,
      })
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
      return
    }

    ensureRecaptchaScript()

    const currentContainer = containerRef.current
    const scriptSrc = `${TRIPLESEAT_SCRIPT_BASE}?lead_form_id=${encodeURIComponent(leadFormId)}&public_key=${encodeURIComponent(publicKey)}`

    // Clear any previous content (HMR, re-renders)
    currentContainer.innerHTML = ''

    const script = document.createElement('script')
    script.src = scriptSrc
    script.async = true

    // Poll for Tripleseat's injected form element (#tripleseat_embed_form)
    // so we know when to hide the spinner.
    let settled = false
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null

    const markReady = () => {
      if (settled) return
      settled = true
      if (pollTimer) clearInterval(pollTimer)
      if (timeoutTimer) clearTimeout(timeoutTimer)
      setIsLoading(false)
    }

    const markError = (message: string, detail?: unknown) => {
      if (settled) return
      settled = true
      if (pollTimer) clearInterval(pollTimer)
      if (timeoutTimer) clearTimeout(timeoutTimer)
      console.error(message, detail)
      setError('Form temporarily unavailable. Please try again later.')
      setIsLoading(false)
    }

    script.onerror = () => {
      markError('Failed to load Tripleseat form script from:', scriptSrc)
    }

    script.onload = () => {
      pollTimer = setInterval(() => {
        if (currentContainer.querySelector('#tripleseat_embed_form')) {
          markReady()
        }
      }, 100)

      timeoutTimer = setTimeout(() => {
        if (currentContainer.querySelector('#tripleseat_embed_form')) {
          markReady()
        } else {
          markError('Tripleseat form did not render within 10 seconds')
        }
      }, 10000)
    }

    // Append to the container (not body) so Tripleseat's script can position
    // its form relative to its own location via document.currentScript.
    currentContainer.appendChild(script)

    // Tripleseat's .timeselect popover is placed at document-absolute coords
    // that go off when the form lives inside a positioned React ancestor.
    // Fix: track the last-focused input, then poll - every 50ms - for a
    // visible .timeselect and pin its top/left/width to the input's real
    // bounding rect. Polling (vs. just listeners) avoids any bet on which
    // event (focus, click, touchend) Tripleseat uses to open the popover.
    //
    // The pin interval only runs while a .timeselect popover is actually in
    // the DOM: focusing a .timeselect_input starts it, and it self-stops
    // once no visible .timeselect remains.
    let lastFocusedInput: HTMLInputElement | null = null
    let pinIntervalId: ReturnType<typeof setInterval> | null = null

    const getVisibleTimeselects = () =>
      Array.from(document.querySelectorAll<HTMLElement>('.timeselect')).filter(
        (el) => el.style.display !== 'none' && (el.offsetParent !== null || el.offsetWidth > 0),
      )

    const pinVisibleTimeselect = () => {
      if (!lastFocusedInput || !document.body.contains(lastFocusedInput)) return
      const visible = getVisibleTimeselects()
      if (visible.length === 0) return
      const rect = lastFocusedInput.getBoundingClientRect()
      if (rect.width === 0) return
      const top = `${rect.bottom + window.scrollY + 4}px`
      const left = `${rect.left + window.scrollX}px`
      const width = `${rect.width}px`
      for (const timeselect of visible) {
        if (
          timeselect.style.top === top &&
          timeselect.style.left === left &&
          timeselect.style.width === width
        ) {
          continue
        }
        timeselect.style.setProperty('top', top, 'important')
        timeselect.style.setProperty('left', left, 'important')
        timeselect.style.setProperty('width', width, 'important')
      }
    }

    const stopPinning = () => {
      if (pinIntervalId === null) return
      clearInterval(pinIntervalId)
      pinIntervalId = null
    }

    const ensurePinning = () => {
      if (pinIntervalId !== null) return
      pinIntervalId = setInterval(() => {
        if (getVisibleTimeselects().length === 0) {
          stopPinning()
          return
        }
        pinVisibleTimeselect()
      }, 50)
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null
      if (target instanceof HTMLInputElement) {
        lastFocusedInput = target
        if (target.classList.contains('timeselect_input')) ensurePinning()
      }
    }

    document.addEventListener('focusin', handleFocusIn, true)
    window.addEventListener('resize', pinVisibleTimeselect)
    window.addEventListener('scroll', pinVisibleTimeselect, true)

    return () => {
      if (pollTimer) clearInterval(pollTimer)
      if (timeoutTimer) clearTimeout(timeoutTimer)
      stopPinning()
      document.removeEventListener('focusin', handleFocusIn, true)
      window.removeEventListener('resize', pinVisibleTimeselect)
      window.removeEventListener('scroll', pinVisibleTimeselect, true)
      if (currentContainer) {
        currentContainer.innerHTML = ''
      }
    }
  }, [leadFormId, publicKey])

  return {
    containerRef,
    isLoading,
    error,
  }
}
